import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { CheckCircle2, Loader2, MessageCircle, ShieldAlert } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { DISCORD_URL } from "@/lib/images";

export default function DiscordConnectCard({ required = false }) {
  const { refresh } = useAuth();
  const [params] = useSearchParams();
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/auth/discord/status").then(({ data }) => setStatus(data)).catch(() => {});
  }, []);

  useEffect(() => {
    const result = params.get("discord");
    if (!result) return;
    if (result === "connected") toast.success("Discord connected — you joined the GZ server");
    else if (result === "connected_join_failed") toast.warning("Discord connected, but auto-join failed. Use the invite link.");
    else toast.error("Discord connection failed — try again");
    refresh();
    api.get("/auth/discord/status").then(({ data }) => setStatus(data)).catch(() => {});
  }, [params, refresh]);

  const connect = async () => {
    setBusy(true);
    try {
      const { data } = await api.get("/auth/discord/connect");
      window.location.href = data.url;
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Discord is not configured");
      setBusy(false);
    }
  };

  const connected = Boolean(status?.connected);
  return (
    <section className={`rounded-2xl border p-5 sm:p-6 ${required && !connected ? "border-amber-300/40 bg-amber-400/[0.04]" : "border-white/10 bg-white/[0.02]"}`}
      data-testid="discord-connect-card">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div className="flex min-w-0 items-center gap-4">
          {connected ? (
            <img src={status.avatar_url} alt="" data-testid="discord-avatar" className="h-12 w-12 rounded-full border border-emerald-300/30 object-cover" />
          ) : (
            <span className="flex h-12 w-12 items-center justify-center rounded-full border border-white/15 text-white/50"><MessageCircle size={20} /></span>
          )}
          <div className="min-w-0">
            <div className="font-mono text-[10px] tracking-[0.22em] text-white/40">DISCORD VERIFICATION</div>
            {connected ? (
              <div className="mt-1 truncate font-display text-xl uppercase" data-testid="discord-connected-name">
                {status.global_name || status.username}
              </div>
            ) : (
              <div className="mt-1 font-display text-xl uppercase">Connect Discord to purchase</div>
            )}
            <div className="mt-1 font-mono text-[10px] tracking-[0.12em] text-white/35">
              {connected ? `@${status.username} · ID ${status.id}` : "REQUIRED BEFORE CHECKOUT · AUTO-JOINS GZ"}
            </div>
          </div>
        </div>
        {connected ? (
          <span className="inline-flex items-center gap-2 rounded-full border border-emerald-300/30 px-3 py-2 font-mono text-[10px] tracking-[0.18em] text-emerald-200" data-testid="discord-connected-badge">
            <CheckCircle2 size={14} /> VERIFIED
          </span>
        ) : (
          <button onClick={connect} disabled={busy} data-testid="discord-connect-btn"
            className="btn-inj inline-flex items-center gap-2 px-5 py-3 text-xs disabled:opacity-60">
            {busy ? <Loader2 size={14} className="animate-spin" /> : <MessageCircle size={14} />} CONNECT DISCORD
          </button>
        )}
      </div>
      {connected && status.joined === false && (
        <div className="mt-4 flex flex-wrap items-center justify-between gap-3 border border-amber-300/25 bg-amber-400/[0.04] px-4 py-3" data-testid="discord-join-warning">
          <span className="inline-flex items-center gap-2 font-mono text-[10px] tracking-[0.14em] text-amber-100/80"><ShieldAlert size={13} /> AUTO-JOIN FAILED — JOIN MANUALLY</span>
          <a href={DISCORD_URL} target="_blank" rel="noreferrer" className="font-mono text-[10px] tracking-[0.14em] text-white underline">OPEN DISCORD INVITE</a>
        </div>
      )}
    </section>
  );
}
